import { COMMAND_CENTER_ROOT } from '../commandCenterRoutes'
import { useState } from 'react'
import CommsFrame from './CommsShell'
import { StatusBadge } from '../operations'
import type { TeamTask, TaskStatus } from './commsData'

/**
 * TaskDetail — /square/command-center/team/tasks/:id.
 *
 * PHASE_2I: presentational, No-Dead-Ends. Change status (open/in_progress/
 * blocked/done), set a blocker reason, assign, edit priority/due. Every change
 * persists (audited) via the route wrapper's callbacks. Receipts are the
 * automatic OLIVIUM audit trail (surfaced note, not fabricated). Send-to-job
 * is a labelled deferred seam.
 *
 * Chat #2 conversion: presentation lives in comms-surface.css. The status
 * selector is a feature control (.sqv-comms-statusbtn[data-status][data-active])
 * with the exact amber/cyan/red/green palette; no hex literals remain here.
 */
export interface TaskDetailProps {
  task?: TeamTask | null
  loading: boolean
  error: string | null
  authRequired: boolean
  onStatus?: (status: TaskStatus) => void | Promise<unknown>
  onSetBlocker?: (reason: string) => void | Promise<unknown>
  onAssign?: (assignee: string) => void | Promise<unknown>
  onHome?: () => void
  onBackCommandCenter?: () => void
  onBackTeam?: () => void
  onNavTop?: (to: string) => void
}

const STATUSES: TaskStatus[] = ['open', 'in_progress', 'blocked', 'done']

export default function TaskDetail(props: TaskDetailProps) {
  const { task, loading, error, authRequired, onStatus, onSetBlocker, onAssign, onHome, onBackCommandCenter, onBackTeam, onNavTop } = props
  const [blocker, setBlocker] = useState('')
  const [assignee, setAssignee] = useState('')

  let center: React.ReactNode
  if (authRequired) {
    center = <div className="sqv-comms-card"><h3 className="sqv-comms-title">Sign in required</h3><p className="sqv-comms-m0 sqv-comms-c-3" style={{ fontSize: 12.5, lineHeight: 1.7 }}>Sign in to the live cockpit to open this task — nothing is shown until then.</p></div>
  } else if (loading) {
    center = <div className="sqv-comms-card"><p className="sqv-comms-m0 sqv-comms-c-3" style={{ fontSize: 12.5 }}>Loading task…</p></div>
  } else if (error) {
    center = <div className="sqv-comms-card"><h3 className="sqv-comms-title">Could not load task</h3><p className="sqv-comms-m0 sqv-comms-c-amber" style={{ fontSize: 12.5 }}>{error}</p></div>
  } else if (!task) {
    center = <div className="sqv-comms-card"><h3 className="sqv-comms-title">Task not found</h3><p className="sqv-comms-mb12 sqv-comms-c-3" style={{ fontSize: 12.5, lineHeight: 1.7 }}>That task isn’t available. No dead end — head back to the team.</p><button type="button" onClick={onBackTeam} className="sqv-comms-btn">← Team</button></div>
  } else {
    center = (
      <div className="sqv-surface-comms" style={{ gap: 14 }}>
        <div className="sqv-comms-card">
          <h2 className="sqv-comms-mb8 sqv-comms-c-1" style={{ fontSize: 18, fontWeight: 900 }}>{task.title}</h2>
          {task.description && <p className="sqv-comms-m0 sqv-comms-c-2" style={{ fontSize: 13, lineHeight: 1.6 }}>{task.description}</p>}
        </div>
        <div className="sqv-comms-card">
          <h3 className="sqv-comms-title">Status</h3>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
            {STATUSES.map((s) => (
              <button key={s} type="button" onClick={() => onStatus?.(s)} className="sqv-comms-statusbtn" data-status={s} data-active={task.status === s ? 'true' : 'false'}>{s.replace('_', ' ').toUpperCase()}</button>
            ))}
          </div>
        </div>
        <div className="sqv-comms-card">
          <h3 className="sqv-comms-title">Blocker</h3>
          {task.status === 'blocked' && task.blocker_reason && <p className="sqv-comms-mb9 sqv-comms-c-amber" style={{ fontSize: 12.5, lineHeight: 1.6 }}>{task.blocker_reason}</p>}
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={blocker} onChange={(e) => setBlocker(e.target.value)} placeholder="Why is this blocked?" aria-label="Blocker reason" className="sqv-comms-input" style={{ flex: 1 }} />
            <button type="button" onClick={() => { if (blocker.trim()) { onSetBlocker?.(blocker.trim()); setBlocker('') } }} className="sqv-comms-btn sqv-comms-btn-danger">Flag blocker</button>
          </div>
        </div>
        <div className="sqv-comms-card">
          <h3 className="sqv-comms-title">Assign</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            <input value={assignee} onChange={(e) => setAssignee(e.target.value)} placeholder="Assignee user id…" aria-label="Assignee user id" className="sqv-comms-input" style={{ flex: 1 }} />
            <button type="button" onClick={() => { if (assignee.trim()) { onAssign?.(assignee.trim()); setAssignee('') } }} className="sqv-comms-btn">Assign</button>
          </div>
          <p className="sqv-comms-hint sqv-comms-c-4 sqv-comms-mono" style={{ fontSize: 10.5 }}>Assigns a real user (audited). A member picker lands with the roster UI.</p>
        </div>
      </div>
    )
  }

  const rightRail = task ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div className="sqv-comms-card">
        <h3 className="sqv-comms-title">Details</h3>
        <div className="sqv-comms-c-2" style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 12 }}>
          <div>Priority: <strong className="sqv-comms-c-1">{task.priority}</strong></div>
          <div>Assignee: <strong className="sqv-comms-c-1">{task.assignee_id ? task.assignee_id.slice(0, 8) : 'unassigned'}</strong></div>
          <div>Due: <strong className="sqv-comms-c-1">{task.due_at ? task.due_at.slice(0, 10) : '—'}</strong></div>
          {task.source_channel_id && <div className="sqv-comms-c-4 sqv-comms-mono" style={{ fontSize: 10.5 }}>from channel {task.source_channel_id.slice(0, 8)}</div>}
        </div>
      </div>
      <div className="sqv-comms-card">
        <h3 className="sqv-comms-title">Receipts</h3>
        <p className="sqv-comms-m0 sqv-comms-c-3" style={{ fontSize: 11.5, lineHeight: 1.6 }}>Every change to this task is recorded automatically in the OLIVIUM audit trail. A per-task receipt feed here is planned.</p>
      </div>
      <div className="sqv-comms-card">
        <h3 className="sqv-comms-title">Actions</h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
          <button type="button" className="sqv-comms-btn" onClick={() => { const p = `${COMMAND_CENTER_ROOT}/team/tasks/${task.id}`; void navigator.clipboard?.writeText(typeof window !== 'undefined' ? `${window.location.origin}${p}` : p) }}>Copy link</button>
          <button type="button" className="sqv-comms-btn" onClick={() => { const p = `${COMMAND_CENTER_ROOT}/team/tasks/${task.id}`; const u = typeof window !== 'undefined' ? `${window.location.origin}${p}` : p; window.location.href = `mailto:?subject=${encodeURIComponent(`Task — ${task.title}`)}&body=${encodeURIComponent(u)}` }}>Email</button>
          <button type="button" className="sqv-comms-btn" onClick={() => { const blob = new Blob([JSON.stringify(task, null, 2)], { type: 'application/json' }); const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `task-${task.id.slice(0, 8)}.json`; a.click(); URL.revokeObjectURL(a.href) }}>Download JSON</button>
          <button type="button" disabled title="No task delete API exists yet — BACKEND_DEFERRED, never faked." className="sqv-comms-btn sqv-comms-btn-delete">Delete (BACKEND_DEFERRED)</button>
        </div>
      </div>
      <div className="sqv-comms-card">
        <h3 className="sqv-comms-title">Send to job</h3>
        <div style={{ marginBottom: 8 }}><StatusBadge status="BACKEND_DEFERRED" size="sm" /></div>
        <p className="sqv-comms-m0 sqv-comms-c-3" style={{ fontSize: 11.5, lineHeight: 1.6 }}>The send-to-job seam (sq_post_jobs) is wired at the schema level; the action lands in a later phase.</p>
      </div>
    </div>
  ) : undefined

  return (
    <CommsFrame
      activeTop="team"
      surface="team-task"
      breadcrumb={['COMMAND CENTER', 'TEAM', 'TASK', task ? task.title.toUpperCase().slice(0, 24) : 'DETAIL']}
      title={task ? 'Task' : 'Task'}
      onHome={onHome}
      onBackCommandCenter={onBackCommandCenter}
      onNavTop={onNavTop}
      center={center}
      rightRail={rightRail}
    />
  )
}
